import { AlertCircle, Loader2 } from "lucide-react";
import { useMemo } from "react";

import { MetricKpiCard, resolveMetricRange } from "@/features/dashboard/shell";
import { FreshnessBadge } from "@/features/dashboard/shell/FreshnessBadge";

import {
  useMeddreamCurrentHour,
  useMeddreamHourlyMetrics,
} from "../sources/meddream/hooks/useMeddreamMetrics";
import { MEDDREAM_LABELS } from "../sources/meddream/labels";
import { MeddreamMetricsPermissionError } from "../sources/meddream/services/meddream-metrics";
import { aggregateMeddreamKpis } from "../sources/meddream/utils/aggregate";

import { OverviewSection } from "./OverviewSection";

/**
 * Resumen MedDream del día en el hub (4 KPIs + CTA al detalle del visor).
 */
export function MeddreamOverview(): React.JSX.Element {
  const range = useMemo(() => resolveMetricRange("today"), []);
  const { data: metrics = [], isLoading, error } = useMeddreamHourlyMetrics(range);
  const { data: current } = useMeddreamCurrentHour();

  const kpis = useMemo(
    () =>
      aggregateMeddreamKpis(metrics, range.startMs ?? 0, range.endMs ?? Date.now()),
    [metrics, range.startMs, range.endMs]
  );

  const isPermission =
    error instanceof MeddreamMetricsPermissionError ||
    (error instanceof Error && /permiso|permission/i.test(error.message));

  return (
    <OverviewSection
      title="Visor MedDream"
      description="Carga del día · solo lectura"
      action={{ label: "Ver métricas del visor", to: "/dashboard/visor" }}
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <FreshnessBadge updatedAt={current?.updatedAt} />
        {isLoading && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Cargando…
          </span>
        )}
      </div>

      {error ? (
        <div className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            {isPermission
              ? "Sin permiso para leer métricas del visor."
              : error.message}
          </p>
        </div>
      ) : isLoading ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-24 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800"
            />
          ))}
        </div>
      ) : metrics.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Sin datos de hoy todavía. Abrí el detalle del visor o esperá al cron.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <MetricKpiCard
            label={MEDDREAM_LABELS.peakConnections.label}
            value={kpis.peakConnections}
            hint={MEDDREAM_LABELS.peakConnections.description}
            sparkline={kpis.sparkPeak}
          />
          <MetricKpiCard
            label={MEDDREAM_LABELS.avgConnections.label}
            value={kpis.avgConnections}
            hint={MEDDREAM_LABELS.avgConnections.description}
            sparkline={kpis.sparkAvg}
          />
          <MetricKpiCard
            label={MEDDREAM_LABELS.sessionActivity.label}
            value={kpis.sessionHourActivity}
            hint={MEDDREAM_LABELS.sessionActivity.description}
            sparkline={kpis.sparkSessions}
          />
          <MetricKpiCard
            label={MEDDREAM_LABELS.restarts.label}
            value={kpis.restartsTotal}
            hint={`${MEDDREAM_LABELS.coverage.label}: ${Math.round(kpis.coverageRatio * 100)}%`}
            sparkline={kpis.sparkRestarts}
            accentClassName={
              kpis.restartsTotal > 0 ? "text-red-600 dark:text-red-400" : undefined
            }
          />
        </div>
      )}
    </OverviewSection>
  );
}
